import { ref, watch, type Ref } from 'vue';
import type { useVehicleData } from '@/composables/useVehicleData';
import type { useQuoteStore } from '@/stores/quoteStore';

type QuoteStore = ReturnType<typeof useQuoteStore>;
type VehicleData = ReturnType<typeof useVehicleData>;

export interface VehicleSelectionDeps {
  quoteStore: QuoteStore;
  vehicleData: VehicleData;
  showPersonalData: Ref<boolean>;
}

const createChangeHandlers = (deps: VehicleSelectionDeps, selected: SelectedIds) => {
  const { quoteStore, vehicleData, showPersonalData } = deps;

  const handleYearChange = async (year: number | null): Promise<void> => {
    if (year) {
      quoteStore.setYear(year);
      selected.brandId.value = '';
      selected.typeId.value = '';
      selected.modelId.value = '';
      showPersonalData.value = false;
      vehicleData.resetAll();
      await vehicleData.loadBrands(year);
    } else {
      vehicleData.resetAll();
    }
  };

  const handleBrandChange = async (brandId: string): Promise<void> => {
    const brand = vehicleData.brands.value.find((b) => b.id === brandId);

    if (brand) {
      quoteStore.setBrand(brand);
      selected.typeId.value = '';
      selected.modelId.value = '';
      showPersonalData.value = false;
      vehicleData.resetTypes();
      vehicleData.resetModels();
      await vehicleData.loadTypes(quoteStore.quoteData.year!, brand.id);
    }
  };

  return { handleYearChange, handleBrandChange };
};

const createModelHandlers = (deps: VehicleSelectionDeps, selected: SelectedIds) => {
  const { quoteStore, vehicleData, showPersonalData } = deps;

  const handleTypeChange = async (typeId: string): Promise<void> => {
    const year = quoteStore.quoteData.year;
    const brandId = quoteStore.quoteData.brand?.id;

    if (year && brandId && typeId) {
      quoteStore.quoteData.type = typeId;
      selected.modelId.value = '';
      showPersonalData.value = false;
      vehicleData.resetModels();
      await vehicleData.loadModels(year, brandId, typeId);
    }
  };

  const handleModelChange = async (modelId: string): Promise<void> => {
    const model = vehicleData.models.value.find((m) => m.id === modelId);

    if (model) {
      quoteStore.setModel(model);
      showPersonalData.value = false;
    }
  };

  return { handleTypeChange, handleModelChange };
};

interface SelectedIds {
  year: Ref<number | null>;
  brandId: Ref<string>;
  typeId: Ref<string>;
  modelId: Ref<string>;
}

const watchYearAndBrand = (deps: VehicleSelectionDeps, selected: SelectedIds): void => {
  const { quoteStore, vehicleData } = deps;

  watch(
    () => quoteStore.quoteData.year,
    (year) => {
      selected.year.value = year;
      if (year) {
        vehicleData.loadBrands(year);
      }
    },
    { immediate: true },
  );

  watch(
    () => quoteStore.quoteData.brand,
    (brand) => {
      selected.brandId.value = brand?.id || '';
      if (brand && quoteStore.quoteData.year) {
        vehicleData.loadTypes(quoteStore.quoteData.year, brand.id);
      }
    },
    { immediate: true },
  );
};

const watchTypeAndModel = (deps: VehicleSelectionDeps, selected: SelectedIds): void => {
  const { quoteStore, vehicleData } = deps;

  watch(
    () => quoteStore.quoteData.type,
    (type) => {
      selected.typeId.value = type || '';
      if (type && quoteStore.quoteData.brand?.id && quoteStore.quoteData.year) {
        vehicleData.loadModels(quoteStore.quoteData.year, quoteStore.quoteData.brand.id, type);
      }
    },
    { immediate: true },
  );

  watch(
    () => quoteStore.quoteData.model,
    (model) => {
      selected.modelId.value = model?.id || '';
    },
    { immediate: true },
  );
};

/** Selected vehicle ids, change handlers and the store -> selection watchers. */
export const useVehicleSelection = (deps: VehicleSelectionDeps) => {
  const selected: SelectedIds = {
    year: ref<number | null>(null),
    brandId: ref<string>(''),
    typeId: ref<string>(''),
    modelId: ref<string>(''),
  };

  const handlers = {
    ...createChangeHandlers(deps, selected),
    ...createModelHandlers(deps, selected),
  };
  watchYearAndBrand(deps, selected);
  watchTypeAndModel(deps, selected);

  return {
    selectedYear: selected.year,
    selectedBrandId: selected.brandId,
    selectedTypeId: selected.typeId,
    selectedModelId: selected.modelId,
    ...handlers,
  };
};
